<template>
  <div class="input-wrapper">
    <RygenButton
      icon="pi pi-download"
      mode="tertiary"
      :label="t('exportSearchResult.exportSearchResult')"
      :loading="inProgressStatus"
      @click="exportSearchResults"
    />

    <ExportSearchResultModal
      v-model:visible="processing"
      :job-status="jobExecutionDetails"
      :document="orderDocument"
    />
  </div>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { computed, PropType, Ref, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import {
  BatchExportDocumentReference,
  BatchJobExecutionDetails,
  BatchStatus,
  ColumnSpecification,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { useElasticsearch } from '@/composables';
import { ORDER_COLUMNS, OrderSearchCriteria } from '@/model';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import ExportSearchResultModal from '@rygen/rygen-ui/components/ui/ExportSearchResultModal.vue';

const props = defineProps({
  criteria: {
    type: OrderSearchCriteria,
    required: true,
  },
  visibleColumns: {
    type: Array as PropType<ColumnSpecification[]>,
    default: () => ORDER_COLUMNS,
  },
});
const { criteria, visibleColumns } = toRefs(props);

const { t } = useI18n();
const toast = useToast();
const { isElasticsearchEnabled } = useElasticsearch({ searchType: 'order' });
const jobExecutionDetails: Ref<BatchJobExecutionDetails> = ref(
  new BatchJobExecutionDetails(),
);
const processing = ref(false);
const orderDocument: Ref<BatchExportDocumentReference> = ref(
  new BatchExportDocumentReference(),
);

const inProgressStatus = computed(() => {
  return (
    jobExecutionDetails.value.status === BatchStatus.STARTING ||
    jobExecutionDetails.value.status === BatchStatus.STARTED ||
    jobExecutionDetails.value.status === BatchStatus.STOPPING
  );
});

const doFetchJobStatus = async (jobId: number): Promise<void> => {
  jobExecutionDetails.value = isElasticsearchEnabled.value
    ? await api.orderSearch.getJobStatus(jobId)
    : await api.orderSearchExport.getJobStatus(jobId);

  if (!jobExecutionDetails.value) {
    resetState();
    return;
  }

  if (jobExecutionDetails.value.isFinalState()) {
    orderDocument.value = await fetchDocument(jobId);
  } else {
    setTimeout(() => doFetchJobStatus(jobId), 2000);
  }
};

const fetchDocument = async (
  jobId: number,
): Promise<BatchExportDocumentReference> => {
  try {
    return isElasticsearchEnabled.value
      ? await api.orderSearch.getDocument(jobId)
      : await api.orderSearchExport.getDocument(jobId);
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return Promise.reject('request canceled');
    }

    toast.addError({
      detail: t('errors.downloadingParam', {
        param: t('common.records', 2),
      }),
      error: e,
    });
    throw e;
  }
};

const resetState = () => {
  jobExecutionDetails.value = new BatchJobExecutionDetails();
};

const exportSearchResults = async () => {
  processing.value = true;
  doExport();
};

const doExport = async () => {
  try {
    jobExecutionDetails.value = isElasticsearchEnabled.value
      ? await api.orderSearch.export(criteria.value, visibleColumns.value)
      : await api.orderSearchExport.export(
          criteria.value,
          visibleColumns.value,
        );
  } catch (e: any) {
    toast.addError({
      error: e,
      detail: t('exportSearchResult.errors.processState', {
        param: t('orders.orders', 2),
      }),
    });
    throw e;
  }
};

watch(
  () => jobExecutionDetails.value?.jobId,
  () => {
    if (jobExecutionDetails.value?.jobId) {
      doFetchJobStatus(jobExecutionDetails.value?.jobId);
    }
  },
  { immediate: true },
);

watch(processing, () => {
  if (!processing.value) {
    resetState();
  }
});
</script>
